import React, { useState, useEffect, useCallback } from 'react';
import { Filter, X, Download, Calendar, Loader2, AlertCircle, RefreshCw, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import { announcementsAPI, getErrorMessage } from '../../lib/api';

const CorporateAnnouncementDashboard = () => {
  // State
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 1,
  });
  
  // Filter state - month selector (current month by default)
  const currentDate = new Date();
  const [filters, setFilters] = useState({
    month: currentDate.getMonth() + 1, // 1-12
    year: currentDate.getFullYear(),
  });

  // Month options
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  // Year options (last 5 years)
  const years = Array.from({ length: 5 }, (_, i) => currentDate.getFullYear() - i);

  // Fetch announcements
  const fetchAnnouncements = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await announcementsAPI.getAnnouncements({
        month: filters.month,
        year: filters.year,
        page: pagination.page,
        page_size: pagination.pageSize,
      });

      // API returns { success: true, data: [...], pagination: {...} }
      const data = response.data?.data || response.data?.results || [];
      const paginationData = response.data?.pagination;
      
      setAnnouncements(Array.isArray(data) ? data : []);
      
      if (paginationData) {
        setPagination((prev) => ({
          ...prev,
          total: paginationData.total || 0,
          totalPages: paginationData.totalPages || 1,
        }));
      }
    } catch (err) {
      console.error('Failed to fetch announcements:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [filters.month, filters.year, pagination.page, pagination.pageSize]);

  // Fetch on mount and filter change
  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: parseInt(value, 10),
    }));
    // Reset to page 1 when filters change
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Clear filters - reset to current month
  const handleClearFilters = () => {
    setFilters({
      month: currentDate.getMonth() + 1,
      year: currentDate.getFullYear(),
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  const handlePrevPage = () => {
    if (pagination.page > 1) {
      handlePageChange(pagination.page - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.totalPages) {
      handlePageChange(pagination.page + 1);
    }
  };

  // Handle PDF download
  const handleDownload = async (announcement) => {
    if (!announcement.has_file) return;
    
    setDownloadingId(announcement.id);
    
    try {
      const response = await announcementsAPI.downloadAnnouncement(announcement.id);
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = announcement.file_name_display || `announcement_${announcement.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download:', err);
      alert(getErrorMessage(err));
    } finally {
      setDownloadingId(null);
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="animate-spin mx-auto mb-3 text-blue-600" size={48} />
          <p className="text-gray-600">Loading announcements...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-3 text-red-500" size={48} />
          <p className="text-red-600 font-semibold mb-2">Failed to load announcements</p>
          <p className="text-gray-500 text-sm mb-4">{error}</p>
          <button
            onClick={fetchAnnouncements}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
          >
            <RefreshCw size={18} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Important Corporate Announcements</h2>
        <p className="text-gray-600">Stay updated with latest corporate announcements</p>
      </div>

      {/* Filter Section */}
      <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Month</label>
            <select
              value={filters.month}
              onChange={(e) => handleFilterChange('month', e.target.value)}
              className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-blue-500 transition-colors"
            >
              {months.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Year</label>
            <select
              value={filters.year}
              onChange={(e) => handleFilterChange('year', e.target.value)}
              className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-blue-500 transition-colors"
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-4 flex items-end gap-2">
            <button
              onClick={fetchAnnouncements}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center justify-center gap-2"
            >
              <Filter size={18} />
              Filter
            </button>
            <button
              onClick={handleClearFilters}
              className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors font-semibold flex items-center justify-center gap-2"
            >
              <X size={18} />
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Announcements Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">S.NO.</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">DATE</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">FILE NAME</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">FILE SIZE</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">DOWNLOAD</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {announcements.map((announcement, index) => (
                <tr key={announcement.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {((pagination.page - 1) * pagination.pageSize) + index + 1}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} />
                      {announcement.formatted_date || '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <span className="flex items-center gap-2">
                      <FileText size={16} className="text-red-500" />
                      {announcement.file_name_display || 'No file'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatFileSize(announcement.file_size)}
                  </td>
                  <td className="px-6 py-4">
                    {announcement.has_file ? (
                      <button
                        onClick={() => handleDownload(announcement)}
                        disabled={downloadingId === announcement.id}
                        className="bg-gray-800 text-white px-4 py-2 rounded-lg hover:bg-gray-900 transition-colors font-semibold text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {downloadingId === announcement.id ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />
                            Downloading...
                          </>
                        ) : (
                          <>
                            <Download size={16} />
                            Download
                          </>
                        )}
                      </button>
                    ) : (
                      <span className="text-gray-400 text-sm">No file</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {announcements.length === 0 && (
          <div className="text-center py-12">
            <FileText className="mx-auto mb-3 text-gray-300" size={48} />
            <p className="text-gray-500 text-lg">No announcements found</p>
            <p className="text-gray-400 text-sm">
              No announcements available for {months.find(m => m.value === filters.month)?.label} {filters.year}
            </p>
          </div>
        )}

        {/* Pagination Controls */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              Showing {((pagination.page - 1) * pagination.pageSize) + 1} to {Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total} announcements
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevPage}
                disabled={pagination.page === 1}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  pagination.page === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <ChevronLeft size={16} />
                Previous
              </button>
              
              {/* Page numbers */}
              <div className="flex items-center gap-1">
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                      pageNum === pagination.page
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>

              <button
                onClick={handleNextPage}
                disabled={pagination.page === pagination.totalPages}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  pagination.page === pagination.totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600 mb-1">Total Announcements</p>
          <p className="text-3xl font-bold text-gray-900">{pagination.total || announcements.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600 mb-1">With Files</p>
          <p className="text-3xl font-bold text-green-600">
            {announcements.filter((a) => a.has_file).length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600 mb-1">Selected Month</p>
          <p className="text-3xl font-bold text-blue-600">
            {months.find(m => m.value === filters.month)?.label.slice(0, 3)} {filters.year}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CorporateAnnouncementDashboard;
